import React, { useState, useMemo } from "react";
import api from "../../../../../services/api";
import styles from "./SummaryReport.module.css";
import SummaryReportModal from "./SummaryReportModal";
import * as XLSX from 'xlsx';

const SummaryReport = () => {
  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [colorize, setColorize] = useState(false);
  const [showNotScannedOnly, setShowNotScannedOnly] = useState(false);
  const [filterDriver, setFilterDriver] = useState(""); // для фильтра по водителю
  const [filterLoader, setFilterLoader] = useState(""); // для фильтра по грузчику

  const [modalVisible, setModalVisible] = useState(false);
  const [modalRoute, setModalRoute] = useState(null);

  // Функция загрузки данных
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/routes/by-date", {
        params: {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
        },
      });
      setRoutes(res.data || res);
    } catch (err) {
      console.error("Ошибка загрузки маршрутов для отчёта", err);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
  // 1) Готовим данные для конвертации
const dataForExcel = filteredRows.map(row => {
  // для каждой позиции считаем, сколько осталось не отсканированных
  const items = row.productsArray
    .map(p => {
      const notScanned = p.totalQty - p.totalScanned;
      if (showNotScannedOnly) {
        // если включена галочка «только неотсканированные»
        return notScanned > 0
          ? `${p.productName}: ${notScanned}`
          : null;
      } else {
        // обычный режим
        return `${p.productName} ${p.totalScanned}/${p.totalQty}`;
      }
    })
    .filter(Boolean)          // отбросить те, что null
    .join('; ');              // собрать в строку

  return {
    Маршрут: row.route_number,
    Дата: row.route_date,
    Водитель: row.driver,
    'Грузчик 1': row.loader1,
    'Грузчик 2': row.loader2,
    Закрыто: row.closedCount,
    'Не доставлено': row.undeliveredCount,
    Открыта: row.openCount,
    'Отсканировано полностью': row.fullyScannedCount,
    'Отсканировано частично': row.partiallyScannedCount,
    'Не отсканировано': row.notScannedCount,
    Товары: items,
  };
});

  // 2) Создаём книгу и лист
  const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Отчёт');

  // 3) Записываем в бинарный массив и создаём Blob
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });

  // 4) Динамически создаём <a> для скачивания
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report_${new Date().toISOString().slice(0,10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

  // Форматирование даты ISO → дд.мм.гг
  const formatDateDMY = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
  };

  // Уникальный список водителей для селекта
  const driversList = useMemo(() => {
    const setNames = new Set();
    routes.forEach((r) => {
      if (r.driver) setNames.add(r.driver);
    });
    return Array.from(setNames).sort();
  }, [routes]);

  // Уникальный список всех грузчиков (loader1 и loader2)
  const loadersList = useMemo(() => {
    const setNames = new Set();
    routes.forEach((r) => {
      if (r.loader1) setNames.add(r.loader1);
      if (r.loader2) setNames.add(r.loader2);
    });
    return Array.from(setNames).sort();
  }, [routes]);

  // Сводные данные по маршрутам
  const summaryRows = useMemo(() => {
    return routes.map((route) => {
      const routeDate = route.date || route.route_date || null;

      let closedCount = 0;
      let undeliveredCount = 0;
      let openCount = 0;

      let fullyScannedCount = 0;
      let partiallyScannedCount = 0;
      let notScannedCount = 0;

      // Подсчёт маркированных товаров
      const productMap = {};

      (route.receipts || []).forEach((receipt) => {
        if (receipt.status === "Закрыта") {
          closedCount++;
        } else if (receipt.status === "Не доставлено") {
          undeliveredCount++;
          return;  // <-- пропускаем эту квитанцию при подсчёте сканирования
        }
        else if (receipt.status === "Открыта") openCount++;

        (receipt.products || []).forEach((p) => {
          if (!p.is_marked) return; // пропускаем немаркированные

          const name = p.product_name;
          const qty = Number(p.actual_quantity) || 0;
          const scanned = Array.isArray(p.marks) ? p.marks.length : 0;
          if (!productMap[name]) {
            productMap[name] = { totalQty: qty, totalScanned: scanned };
          } else {
            productMap[name].totalQty += qty;
            productMap[name].totalScanned += scanned;
          }
        });

        // Подсчёт общего статуса сканирования квитанции
        const products = (receipt.products || []).filter(p => p.is_marked);
        if (products.length === 0) {
          notScannedCount++;
        } else {
          let allFully = true;
          let anyScanned = false;
          products.forEach((p) => {
            const scanned = Array.isArray(p.marks) ? p.marks.length : 0;
            const qty = Number(p.actual_quantity) || 0;
            if (scanned < qty) allFully = false;
            if (scanned > 0) anyScanned = true;
          });
          if (allFully) fullyScannedCount++;
          else if (anyScanned) partiallyScannedCount++;
          else notScannedCount++;
        }
      });

      const totalReceipts =
        fullyScannedCount + partiallyScannedCount + notScannedCount;

      const productsArray = Object.entries(productMap).map(
        ([productName, { totalQty, totalScanned }]) => ({
          productName,
          totalQty,
          totalScanned,
        })
      );

      return {
        route_number: route.route_number || "-",
        route_date: formatDateDMY(routeDate),
        driver: route.driver || "-",
        loader1: route.loader1 || "-",
        loader2: route.loader2 || "-",
        closedCount,
        undeliveredCount,
        openCount,
        fullyScannedCount,
        partiallyScannedCount,
        notScannedCount,
        totalReceipts,
        productsArray,
      };
    });
  }, [routes]);

  // Фильтруем сразу по водителю и по грузчику
  const filteredRows = useMemo(() => {
    return summaryRows.filter((row) => {
      const byDriver = filterDriver ? row.driver === filterDriver : true;
      const byLoader = filterLoader
        ? row.loader1 === filterLoader || row.loader2 === filterLoader
        : true;
      return byDriver && byLoader;
    });
  }, [summaryRows, filterDriver, filterLoader]);

  // Генерация CSS-класса для цветового фона ячеек
  const getCellClass = (type, row) => {
    if (!colorize) return "";

    switch (type) {
      case "open":
        return row.openCount > 0 ? styles.redCell : "";
      case "statusAllGreen":
        return row.openCount === 0 ? styles.greenCell : "";
      case "fullyScanned":
        if (row.totalReceipts === 0) return "";
        if (row.fullyScannedCount === row.totalReceipts)
          return styles.greenCell;
        if (row.fullyScannedCount === 0) return styles.redCell;
        return styles.yellowCell;
      case "partialScanned":
        return row.partiallyScannedCount > 0 ? styles.yellowCell : "";
      case "notScanned":
        if (row.totalReceipts === 0) return "";
        if (row.notScannedCount === row.totalReceipts) return styles.redCell;
        if (
          row.notScannedCount > 0 &&
          row.notScannedCount < row.totalReceipts
        )
          return styles.yellowCell;
        return "";
      default:
        return "";
    }
  };

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>Итоги по квитанциям</h2>

      <div className={styles.controls}>
        <label className={styles.label}>
          С:&nbsp;
          <input
            type="date"
            className={styles.dateInput}
            value={startDate.toISOString().split("T")[0]}
            onChange={(e) => setStartDate(new Date(e.target.value))}
          />
        </label>
        <label className={styles.label}>
          По:&nbsp;
          <input
            type="date"
            className={styles.dateInput}
            value={endDate.toISOString().split("T")[0]}
            onChange={(e) => setEndDate(new Date(e.target.value))}
          />
        </label>


        <button
          className={styles.generateButton}
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Загрузка..." : "Сформировать отчёт"}
        </button>
        <button
          className={styles.exportButton}
          onClick={exportToExcel}
          disabled={loading || filteredRows.length === 0}
        >
          Скачать в Excel
        </button>


        {/* Селект для фильтрации по водителю */}
        <label className={styles.label}>
          Водитель:&nbsp;
          <select
            className={styles.filterSelect}
            value={filterDriver}
            onChange={(e) => setFilterDriver(e.target.value)}
          >
            <option value="">Все</option>
            {driversList.map((drv) => (
              <option key={drv} value={drv}>
                {drv}
              </option>
            ))}
          </select>
        </label>

        {/* Селект для фильтрации по грузчику */}
        <label className={styles.label}>
          Грузчик:&nbsp;
          <select
            className={styles.filterSelect}
            value={filterLoader}
            onChange={(e) => setFilterLoader(e.target.value)}
          >
            <option value="">Все</option>
            {loadersList.map((ldr) => (
              <option key={ldr} value={ldr}>
                {ldr}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.colorToggle}>
          <input
            type="checkbox"
            checked={colorize}
            onChange={(e) => setColorize(e.target.checked)}
          />{" "}
          Включить цветовое отображение
        </label>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={showNotScannedOnly}
            onChange={e => setShowNotScannedOnly(e.target.checked)}
          />{' '}
          Показывать только неотсканированные
        </label>
      </div>

      {loading ? (
        <div className={styles.loading}>Загрузка данных...</div>
      ) : filteredRows.length > 0 ? (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Маршрут</th>
                <th>Дата</th>
                <th>Водитель</th>
                <th>Грузчик 1</th>
                <th>Грузчик 2</th>
                <th className={styles.statusHeader}>Закрыто</th>
                <th className={styles.statusHeader}>Не доставлено</th>
                <th className={styles.statusHeader}>Открыта</th>
                <th>Отсканировано полностью</th>
                <th>Отсканировано частично</th>
                <th>Не отсканировано</th>
                <th className={styles.productsHeader}>Товары (скан)</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.route_number}</td>
                  <td>{row.route_date}</td>
                  <td>{row.driver}</td>
                  <td>{row.loader1}</td>
                  <td>{row.loader2}</td>

                  <td className={getCellClass("statusAllGreen", row)}>
                    {row.closedCount}
                  </td>
                  <td className={getCellClass("statusAllGreen", row)}>
                    {row.undeliveredCount}
                  </td>
                  <td>
                    {row.openCount > 0 ? (
                      <button
                        className={styles.openButton}
                        onClick={() => {
                          // fullRoute — это именно тот объект из массива routes
                          const fullRoute = routes.find(
                            r => r.route_number === row.route_number
                          );
                          setModalRoute(fullRoute);      // сохраняем весь объект маршрута
                          setModalVisible(true);         // показываем модалку
                        }}
                      >
                        {row.openCount}
                      </button>
                    ) : (
                      row.openCount
                    )}
                  </td>
                  <td className={getCellClass("fullyScanned", row)}>
                    {row.fullyScannedCount}
                  </td>
                  <td className={getCellClass("partialScanned", row)}>
                    {row.partiallyScannedCount}
                  </td>
                  <td className={getCellClass("notScanned", row)}>
                    {row.notScannedCount}
                  </td>
                  <td className={styles.productsCell}>
                    {row.productsArray.map((p, i) => {
                      const notScanned = p.totalQty - p.totalScanned;

                      if (showNotScannedOnly) {
                        // Если включена опция «Показывать только неотсканированные»
                        if (notScanned === 0) {
                          return null; // не рисуем, если ничего не осталось
                        }
                        // показываем только неотскананных, синий текст
                        return (
                          <div key={i} className={styles.blueText}>
                            {p.productName}: {notScanned}
                          </div>
                        );
                      }

                      // обычный режим: скан/всего + цветовая логика
                      let cls = "";
                      if (p.totalScanned === 0) cls = styles.redText;
                      else if (p.totalScanned === p.totalQty) cls = styles.greenText;
                      else cls = styles.yellowText;

                      return (
                        <div key={i} className={cls}>
                          {`${p.productName} ${p.totalScanned}/${p.totalQty}`}
                        </div>
                      );
                    })}
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.noData}>Нажмите «Сформировать отчёт»</div>
      )}
        <SummaryReportModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          route={modalRoute}
        />
    </div>
  );
};

export default SummaryReport;
